import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  CalendarIcon, Plus, Star, AlertTriangle, MessageSquareX, Clock, XCircle,
  Trophy, Award, TrendingUp, Users, Handshake, GraduationCap, Briefcase,
  UserCheck, Heart, ShieldCheck, Phone, Smile, Lightbulb,
  MessageCircle, Eye, Shield, Siren, Zap, PlusCircle, LogOut, Ban,
  OctagonX, Moon, UserX, FileX, FileWarning, TrendingDown,
  Frown, UserMinus, AlertCircle as AlertCircleIcon, ShieldAlert, EyeOff,
  PackageX, Wrench, PhoneOff
} from 'lucide-react';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useBehaviorTypes, useCreateBehaviorRecord, BehaviorType } from '@/hooks/useBehaviorTracking';
import { useLanguage } from '@/contexts/LanguageContext';
import { useQueryClient } from '@tanstack/react-query';

interface BehaviorTrackingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guardId?: string;
  guardName?: string;
  teamId?: string;
}

export const BehaviorTrackingModal: React.FC<BehaviorTrackingModalProps> = ({
  open,
  onOpenChange,
  guardId,
  guardName,
  teamId,
}) => {
  const [selectedBehaviorType, setSelectedBehaviorType] = useState<string>('');
  const [incidentDate, setIncidentDate] = useState<Date>(new Date());
  const [description, setDescription] = useState('');
  const [evidence, setEvidence] = useState('');
  const [category, setCategory] = useState<'positive' | 'negative' | 'all'>('all');
  const { language, t } = useLanguage();

  const getLocalizedName = (type: BehaviorType) => {
    if (language === 'el') return type.name_el || type.name;
    if (language === 'en') return type.name_en || type.name;
    if (language === 'fr') return type.name_fr || type.name;
    return type.name;
  };

  const getLocalizedDescription = (type: BehaviorType) => {
    if (language === 'el') return type.description_el || type.description;
    if (language === 'en') return type.description_en || type.description;
    if (language === 'fr') return type.description_fr || type.description;
    return type.description;
  };

  const queryClient = useQueryClient();
  const { data: behaviorTypes = [], isLoading, refetch } = useBehaviorTypes();
  const { mutate: createBehaviorRecord, isPending } = useCreateBehaviorRecord();
  
  // Force fresh data when modal opens
  React.useEffect(() => {
    if (open) {
      console.log('BehaviorTrackingModal opened - clearing cache and refetching');
      console.log('Current language:', language);
      // Clear the cache and refetch
      queryClient.invalidateQueries({ queryKey: ['behavior-types'] });
      refetch();
    }
  }, [open, refetch, queryClient, language]);
  
  // Log behavior types when they change
  React.useEffect(() => {
    if (behaviorTypes.length > 0) {
      console.log('Behavior types loaded:', behaviorTypes.map(t => ({
        name: t.name,
        name_el: t.name_el,
        name_en: t.name_en,
        category: t.category
      })));
    }
  }, [behaviorTypes]);

  const filteredBehaviorTypes = behaviorTypes.filter(
    (type) => category === 'all' || type.category === category
  );

  const selectedType = behaviorTypes.find(type => type.id === selectedBehaviorType);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!guardId || !selectedBehaviorType) {
      return;
    }

    createBehaviorRecord({
      guardId,
      behaviorTypeId: selectedBehaviorType,
      teamId,
      incidentDate: format(incidentDate, 'yyyy-MM-dd'),
      description: description.trim() || undefined,
      evidence: evidence.trim() || undefined,
    }, {
      onSuccess: () => {
        setSelectedBehaviorType('');
        setDescription('');
        setEvidence('');
        setIncidentDate(new Date());
        onOpenChange(false);
      },
    });
  };

  const getBehaviorIcon = (type: BehaviorType) => {
    // Map icon names to lucide-react components
    const iconMap: Record<string, any> = {
      // Positive icons
      'trophy': Trophy,
      'award': Award,
      'trending-up': TrendingUp,
      'users': Users,
      'handshake': Handshake,
      'graduation-cap': GraduationCap,
      'briefcase': Briefcase,
      'user-check': UserCheck,
      'heart': Heart,
      'clock-check': Clock,
      'shield-check': ShieldCheck,
      'phone': Phone,
      'smile': Smile,
      'lightbulb': Lightbulb,
      'message-circle': MessageCircle,
      'eye': Eye,
      'shield': Shield,
      'siren': Siren,
      'zap': Zap,
      'plus-circle': PlusCircle,
      'star': Star,
      // Negative icons
      'log-out': LogOut,
      'ban': Ban,
      'octagon-x': OctagonX,
      'moon': Moon,
      'user-x': UserX,
      'clock': Clock,
      'clock-alert': Clock,
      'file-x': FileX,
      'file-warning': FileWarning,
      'trending-down': TrendingDown,
      'frown': Frown,
      'user-minus': UserMinus,
      'alert-circle': AlertCircleIcon,
      'shield-alert': ShieldAlert,
      'eye-off': EyeOff,
      'package-x': PackageX,
      'wrench': Wrench,
      'phone-off': PhoneOff,
      'user-x-2': UserX,
      'alert-triangle': AlertTriangle,
      'message-square-x': MessageSquareX,
      'x-circle': XCircle,
    };
    
    if (type.icon && iconMap[type.icon]) {
      const IconComponent = iconMap[type.icon];
      return <IconComponent className="h-5 w-5" />;
    }
    
    // Default icons based on category
    return type.category === 'positive' ? (
      <Star className="h-5 w-5" />
    ) : (
      <AlertTriangle className="h-5 w-5" />
    );
  };

  const getCategoryIcon = (category: 'positive' | 'negative') => {
    return category === 'positive' ? (
      <Star className="h-4 w-4 text-green-600 dark:text-green-400" />
    ) : (
      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            {t('recordBehavior') || 'Καταγραφή Συμπεριφοράς'}
          </DialogTitle>
          <DialogDescription>
            {guardName ? `${t('recordBehaviorFor') || 'Καταγραφή συμπεριφοράς για τον/την'} ${guardName}` : (t('recordEmployeeBehavior') || 'Καταγραφή συμπεριφοράς υπαλλήλου')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Filter */}
          <div className="space-y-2">
            <Label>{t('behaviorCategory') || 'Κατηγορία Συμπεριφοράς'}</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={category === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategory('all')}
              >
                {t('all') || 'Όλες'}
              </Button>
                <Button
                  type="button"
                  variant={category === 'positive' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory('positive')}
                  className="text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-900/20"
                >
                  <Star className="h-4 w-4 mr-1" />
                  {t('positive') || 'Θετικές'}
                </Button>
                <Button
                  type="button"
                  variant={category === 'negative' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory('negative')}
                  className="text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  {t('negative') || 'Αρνητικές'}
                </Button>
            </div>
          </div>

          {/* Behavior Type Selection */}
          <div className="space-y-2">
            <Label>{t('behaviorType') || 'Τύπος Συμπεριφοράς'} *</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-border rounded-md p-2">
              {isLoading ? (
                <div className="col-span-2 text-center py-4 text-muted-foreground">
                  {t('loadingBehaviorTypes') || 'Φόρτωση τύπων συμπεριφοράς...'}
                </div>
              ) : filteredBehaviorTypes.length === 0 ? (
                <div className="col-span-2 text-center py-4 text-muted-foreground">
                  {t('noBehaviorTypesFound') || 'Δεν βρέθηκαν τύποι συμπεριφοράς'}
                </div>
              ) : (
                filteredBehaviorTypes.map((type) => (
                  <div
                    key={type.id}
                    className={cn(
                      'p-3 border rounded-lg cursor-pointer transition-colors hover:bg-muted',
                      selectedBehaviorType === type.id ? 'border-primary bg-primary/5' : 'border-border'
                    )}
                    onClick={() => setSelectedBehaviorType(type.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="text-foreground">{getBehaviorIcon(type)}</div>
                          <span className="font-medium text-sm text-foreground">{getLocalizedName(type)}</span>
                          {getCategoryIcon(type.category)}
                        </div>
                        {getLocalizedDescription(type) && (
                          <p className="text-xs text-muted-foreground mb-2">{getLocalizedDescription(type)}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <Badge
                            variant={type.score_impact > 0 ? 'default' : 'destructive'}
                            className="text-xs"
                          >
                            {type.score_impact > 0 ? '+' : ''}{type.score_impact} πόντοι
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Selected Behavior Type Preview */}
          {selectedType && (
            <div className="p-4 border rounded-lg bg-blue-50/80 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-foreground">{getBehaviorIcon(selectedType)}</div>
                <h4 className="font-medium text-foreground">{getLocalizedName(selectedType)}</h4>
                <Badge
                  variant={selectedType.score_impact > 0 ? 'default' : 'destructive'}
                  className="ml-auto"
                >
                  {selectedType.score_impact > 0 ? '+' : ''}{selectedType.score_impact} πόντοι
                </Badge>
              </div>
              {getLocalizedDescription(selectedType) && (
                <p className="text-sm text-muted-foreground">{getLocalizedDescription(selectedType)}</p>
              )}
            </div>
          )}

          {/* Incident Date */}
          <div className="space-y-2">
            <Label>{t('incidentDate') || 'Ημερομηνία Περιστατικού'} *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !incidentDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {incidentDate ? format(incidentDate, 'PPP', { locale: language === 'el' ? el : enUS }) : (t('selectDate') || 'Επιλέξτε ημερομηνία')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={incidentDate}
                  onSelect={(date) => date && setIncidentDate(date)}
                  initialFocus
                  locale={language === 'el' ? el : enUS}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('incidentDescription') || 'Περιγραφή Περιστατικού'}</Label>
            <Textarea
              id="description"
              placeholder={t('describeIncidentPlaceholder') || 'Περιγράψτε το περιστατικό...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Evidence */}
          <div className="space-y-2">
            <Label htmlFor="evidence">{t('evidenceProof') || 'Στοιχεία/Αποδείξεις'}</Label>
            <Textarea
              id="evidence"
              placeholder={t('describeEvidencePlaceholder') || 'Περιγράψτε τυχόν στοιχεία ή αποδείξεις...'}
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              rows={2}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t('cancel') || 'Ακύρωση'}
            </Button>
            <Button
              type="submit"
              disabled={!guardId || !selectedBehaviorType || isPending}
            >
              {isPending ? (t('recording') || 'Καταγραφή...') : (t('recordBehavior') || 'Καταγραφή Συμπεριφοράς')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};